import React, {useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import {SystemSettingsGetSchema} from '../../../../api/DBSchema/SystemSettings';
import SMCTable from '../../../../components/core/SMCTable/SMCTable';
import SMCLink from '../../../../components/Simple/SMCLink';
import RowLayout from '../../../../components/Layouts/RowLayout';
import SMCButton from '../../../../components/Simple/SMCButton';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import {SMCRadioGroup, SMCRadio} from '../../../../components/Simple/SMCRadio';
import AccessControlEditorModal from '../../../PopupPages/AccessControlEditorModal';
import {useBoolean} from '../../../../utils/hooks';

const pageSchema = SystemSettingsGetSchema.pages.adminAccessControl;
const pageId = pageSchema.pageId;

function SystemSettingsAdminAccessControl({
    systemSettingsObject,
    setSystemSettingsData
}) {
    const { t } = useTranslation();

    const pageData  = systemSettingsObject[pageId];
    const setPageData = useCallback((param, newValue) => {
        setSystemSettingsData([pageId, param], newValue);
    }, [setSystemSettingsData]);

    const accessMethod = pageData[pageSchema.accessMethod];
    const dataList     = pageData[pageSchema.dataList];

    const accessMethodValues = pageSchema.accessMethodValues;
    const dataListSchema     = pageSchema.dataListSchema;

    const dataTableDisabled = accessMethod === accessMethodValues.any;

    const [isAccessEditorOpen, openAccessEditor, closeAccessEditor] = useBoolean(false);
    const [currentlyEditedObject, setCurrentlyEditedObject] = useState(null);
    const [currentlyEditedObjectIndex, setCurrentlyEditedObjectIndex] = useState(null);
    const [selectedObjects, setSelectedObjects] = useState([]);

    const objectEditorOnSuccess = useCallback((val) => {
        const dataListCopy = [...dataList];
        if (currentlyEditedObject) {
            dataListCopy[currentlyEditedObjectIndex] = val;
            setCurrentlyEditedObject(null);
            setCurrentlyEditedObjectIndex(null);
        } else {
            dataListCopy.push(val);
        }
        setPageData(pageSchema.dataList, dataListCopy);
        closeAccessEditor();
    }, [dataList, currentlyEditedObject, currentlyEditedObjectIndex, setPageData, closeAccessEditor]);

    const objectEditorOnClose = useCallback(() => {
        setCurrentlyEditedObject(null);
        setCurrentlyEditedObjectIndex(null);
        closeAccessEditor();
    }, [closeAccessEditor]);

    const deleteSelectedObjects = useCallback(() => {
        if (selectedObjects.length) {
            const newDataList = dataList.filter((obj, index) => {
                return !selectedObjects.includes(index);
            });
            setPageData(pageSchema.dataList, newDataList);
            setSelectedObjects([]);
        }
    }, [dataList, selectedObjects, setPageData]);

    const CountCell = React.memo(({index}) => (
        <SMCLabel bold inline>{index + 1}</SMCLabel>
    ));

    const IpAddressCell = React.memo(({value, object, index}) => (
        <SMCLink
            inline
            disabled={dataTableDisabled}
            onClick={() => {
                setCurrentlyEditedObject(object);
                setCurrentlyEditedObjectIndex(index);
                openAccessEditor();
            }}
        >
            {value}
        </SMCLink>
    ));
    const TypeCell = React.memo(({value}) => {
        switch (value) {
            case 'IP_ADDRESS':
                return t('SMP.SYSTEM.SETTINGS.ACCESS_CONTROL.ADMIN_ACCESS_CONTROL.IP_ADDRESS');
            case 'NETWORK':
                return t('SMP.SYSTEM.SETTINGS.ACCESS_CONTROL.ADMIN_ACCESS_CONTROL.NETWORK');
            default:
                return value;
        }
    });

    return (
        <EditorLayout>
            <SMCRadioGroup
                value={accessMethod}
                onChange={e => setPageData(pageSchema.accessMethod, e.target.value)}
            >
                <SMCRadio
                    value={accessMethodValues.any}
                    label={t('SMP.SYSTEM.SETTINGS.ACCESS_CONTROL.ADMIN_ACCESS_CONTROL.LINE1')}
                />
                <SMCRadio
                    value={accessMethodValues.restricted} 
                    label={t('SMP.SYSTEM.SETTINGS.ACCESS_CONTROL.ADMIN_ACCESS_CONTROL.LINE2')}
                />
            </SMCRadioGroup>
            <RowLayout>
                <SMCButton variant="ghost" icon="new" disabled={dataTableDisabled} onClick={openAccessEditor}>{t('SMC_GEN_NEW')}</SMCButton>
                <SMCButton variant="ghost" icon="delete" disabled={dataTableDisabled} onClick={deleteSelectedObjects}>{t('SMC_GEN_DELETE')}</SMCButton>
            </RowLayout>
            <SMCTable
                columns={[
                    { id: 'count',       header: '#',                                                                           width: 32,                                       align: 'center', Cell: CountCell      },
                    { id: 'ip_address',  header: t('SMP.SYSTEM.SETTINGS.ACCESS_CONTROL.ADMIN_ACCESS_CONTROL.IP_ADDRESS'),  width: 130, accessor: dataListSchema.ipAddr,                      Cell: IpAddressCell  },
                    { id: 'subnet_mask', header: t('SMP.SYSTEM.SETTINGS.ACCESS_CONTROL.ADMIN_ACCESS_CONTROL.SUBNET_MASK'), width: 130, accessor: dataListSchema.subnetMask                                        },
                    { id: 'type',        header: t('SMP.SYSTEM.SETTINGS.ACCESS_CONTROL.ADMIN_ACCESS_CONTROL.TYPE'),        width: 100, accessor: dataListSchema.type,                        Cell: TypeCell       }
                ]}
                content={dataList}
                selectedObjects={selectedObjects}
                setSelectedObjects={setSelectedObjects}
                rowIdSelector={(obj, index) => index}
                hasCheckAll={true}
            />
            {/* Popup windows */}
            <AccessControlEditorModal
                isOpen={isAccessEditorOpen}
                defaultObject={currentlyEditedObject}
                onCancel={objectEditorOnClose}
                onSuccess={objectEditorOnSuccess}
                title={t('SMP.MGMT_ACCESS_WIZ.TITLE')}
            />
        </EditorLayout>
    )
}

export default SystemSettingsAdminAccessControl;